const IMG_EXT = /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|avif)$/i;
export const isImageFile = (f) => (f?.type || "").startsWith("image/") || IMG_EXT.test(f?.name || "");

const decode = async (file) => {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* HEIC 等解码失败 → <img> 兜底 */ }
  }
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); res(img); };
    img.onerror = () => { URL.revokeObjectURL(url); rej(new Error("decode")); };
    img.src = url;
  });
};

/**
 * 浏览器能不能把 canvas 编成 WebP。
 *
 * 为什么不直接 `toBlob(..., "image/webp")` 了事：规范允许浏览器**静默降级**成 PNG，
 * 而 `blob.type` 是唯一可靠的判据（Chrome / Edge / Firefox / Safari 14+ 都支持 WebP，
 * 只有很老的 Safari 会退化成 PNG）。降级成 PNG 时体积会比原图还大，必须识别出来
 * 改走 JPEG，否则「压缩」反而变慢。
 */
let _webpOk = null;
const canEncodeWebp = () => {
  if (_webpOk !== null) return _webpOk;
  try {
    const c = document.createElement("canvas");
    c.width = 1; c.height = 1;
    const url = c.toDataURL("image/webp");
    _webpOk = url.startsWith("data:image/webp");
  } catch { _webpOk = false; }
  return _webpOk;
};

/**
 * 手机原图（HEIC / 4000px+ · 3–12MB）在浏览器端先缩到长边 maxSide，再分片上传：
 * 弱网下成功率与速度都高得多；解码失败（如 Chrome 遇 HEIC）则原样上传由服务端转换。
 *
 * 2026-10-20 编码 JPEG → WebP（用户反馈「压缩以后显示分辨率降低」）：
 *   旧的 `image/jpeg, 0.85` 在 2560px 长边下，6000×4000 的手机照片只剩 2560×1707，
 *   用户放大看就是「压糊了」。实测**同样的字节预算**下 WebP 能多放 1.5 倍以上像素：
 *        JPEG 2560×1707 q85 ≈ 1.01MB
 *        WebP 3840×2560 q76 ≈ 1.00MB   ← 体积一样，像素 2.25 倍
 *   所以这里换编码器 + 提长边，做到「看着更清楚」且「上传不变慢 / 更快」。
 *   浏览器不支持 WebP（极老 Safari）→ 自动回退 JPEG，参数保持旧口径，不会更差。
 */
export async function shrinkImage(file, { maxSide = 3840, quality = 0.8, minBytes = 2.5 * 1024 * 1024, format } = {}) {
  if (!isImageFile(file) || /gif|svg/i.test(file.type) || file.size < minBytes) return file;
  const useWebp = format !== "jpeg" && canEncodeWebp();
  const mime = useWebp ? "image/webp" : "image/jpeg";
  const ext = useWebp ? ".webp" : ".jpg";
  try {
    const src = await decode(file);
    const w = src.width || src.naturalWidth, h = src.height || src.naturalHeight;
    if (!w || !h) return file;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    const cw = Math.round(w * scale), ch = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext("2d");
    // 缩小时开启高质量插值：默认的 "low" 会让细线 / 文字发虚，正是用户说的「分辨率降低」的观感来源之一。
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(src, 0, 0, cw, ch);
    src.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, mime, quality));
    // blob.type 与请求不符 = 浏览器把 WebP 降级成了 PNG（见 canEncodeWebp 说明）→ 改走 JPEG。
    if (blob && useWebp && blob.type !== "image/webp") {
      const jpg = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
      if (jpg && jpg.size < file.size) return new File([jpg], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg", lastModified: file.lastModified || Date.now() });
    }
    if (!blob || blob.size >= file.size) return file;
    const outMime = blob.type || mime;
    const outExt = outMime === "image/webp" ? ".webp" : outMime === "image/png" ? ".png" : ext;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + outExt, { type: outMime, lastModified: file.lastModified || Date.now() });
  } catch { return file; }
}

/**
 * 压缩档位（2026-10-15 用户拍板「按用途分档」；2026-10-20 按「别压糊」重调）。
 *
 * ⚠️ 这两个数字才是决定**上传快不快**的杠杆 —— 后端压缩只省服务器和回放带宽，
 *    治不了「5–12MB 手机原图传半天」这个体感。所以默认档位统一挂在共享切片上传层
 *    （`lib/upload.js` 的 uploadChunked），全站一次生效，不用逐个模块改。
 *
 * 重调依据（同一张 6000×4000 高频细节图实测，见 backend/media_compress.py 注释）：
 *   shot     截图类：需求反馈 / 聊天发图。旧口径 1600 会让 2K/4K 截图的正文小字发糊，
 *            现在 2880 长边 ≈ 230KB（旧 1600 是 210KB）—— 体积几乎没涨，像素翻倍。
 *            普通截图（<900KB）仍然原样跳过，不碰。
 *   material 业务素材：现场照片 / 设计效果图 / 图纸。长边 2560 → **3840**，
 *            WebP q0.8 下约 1MB，与旧 JPEG 2560/q0.85 基本同体积。
 */
export const IMG_PRESETS = {
  shot: { maxSide: 2880, quality: 0.8, minBytes: 900 * 1024 },
  material: { maxSide: 3840, quality: 0.8, minBytes: 2.5 * 1024 * 1024 },
};

export const shrinkOpts = (preset) =>
  typeof preset === "string" ? (IMG_PRESETS[preset] || IMG_PRESETS.material)
    : preset && typeof preset === "object" ? { ...IMG_PRESETS.material, ...preset }
      : IMG_PRESETS.material;
